import type { translations } from '@/translations/translations';
import type { PhraseRecordingStats } from './phrase-recording-stats';

// Same status colours as the contribution statistics.
const STATUS_COLOR = {
  validated: 'hsl(142 76% 36%)',
  pending: 'hsl(38 92% 50%)',
  rejected: 'hsl(0 84% 60%)',
} as const;

interface Props {
  copy: typeof translations.fr.admin.excelPhrases;
  stats: PhraseRecordingStats | undefined;
  isLoading: boolean;
}

/** How much one phrase was recorded: recordings, people, and their validation. */
export function PhraseRecordingSummary({ copy, stats, isLoading }: Props) {
  if (isLoading) return <span className="text-xs text-muted-foreground">{copy.recordingsLoading}</span>;
  if (!stats || stats.recordings === 0) {
    return <span className="text-xs text-muted-foreground">{copy.neverRecorded}</span>;
  }
  const parts = (['validated', 'pending', 'rejected'] as const).filter((status) => stats[status] > 0);
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>
        <span className="font-semibold tabular-nums text-foreground">{stats.recordings}</span> {copy.recordingsLabel}
      </span>
      <span>
        <span className="font-semibold tabular-nums text-foreground">{stats.contributors}</span> {copy.contributorsLabel}
      </span>
      <span className="inline-flex h-1.5 w-20 gap-[2px] overflow-hidden rounded-full bg-muted" aria-hidden>
        {parts.map((status) => (
          <span key={status} style={{ width: `${(stats[status] / stats.recordings) * 100}%`, background: STATUS_COLOR[status] }} />
        ))}
      </span>
      <span>
        {copy.recordingStatus
          .replace('{validated}', String(stats.validated))
          .replace('{pending}', String(stats.pending))
          .replace('{rejected}', String(stats.rejected))}
      </span>
    </span>
  );
}
